import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useOnboardingStore, STEPS } from '../stores/onboardingStore'
import { useStepSubmit } from '../context/StepSubmitContext'
import RowInput from '../components/form/RowInput'
import EmailInputWithDomain from '../components/form/EmailInputWithDomain'
import AddAnotherButton from '../components/form/AddAnotherButton'
import EntryHeader from '../components/form/EntryHeader'
import Field from '../components/form/Field'
import FieldRow from '../components/form/FieldRow'
import Card from '../components/form/Card'
import StepLayout from '../components/form/StepLayout'
import Band from '../components/form/Band'
import ErrorBanner from '../components/form/ErrorBanner'
import { useStepErrorSummary } from '../hooks/useStepErrorSummary'
import { extractEmailDomain, ordinalLabel } from '../lib/formatters'
import {
  flattenErrorCount,
  isValidEmail,
  isValidPhone,
  suggestEmailCorrection,
  uniqueEmailsAcross,
  validateName,
} from '../lib/validators'
import { isValidationDisabled } from '../lib/validationMode'

const STEP_ID = 'teams'

// All 5 Operations Hub permissions, including Subscription Manager which is
// only surfaced when Managed Billing is on. Descriptions live alongside the
// values so the role-explainer panel can iterate the same array.
const ALL_PERMISSIONS = [
  {
    value: 'assistant',
    label: 'Assistant',
    desc: 'Day-to-day patient support — view the patient list, register new patients, view and manage profiles etc.',
  },
  {
    value: 'medical assistant',
    label: 'Medical Assistant',
    desc: 'Everything Assistant can do, plus clinical actions — order labs, view lab results, view and manage health records etc.',
  },
  {
    value: 'finance',
    label: 'Finance',
    desc: 'Billing only — transactions, practice invoices, location wallets, payment history. No clinical or subscription edits.',
  },
  {
    value: 'analytics',
    label: 'Analytics',
    desc: 'Practice analytics — patient counts, retention, revenue trends.',
  },
  {
    value: 'subscription manager',
    label: 'Subscription Manager',
    desc: "Ability to Manage patient subscriptions on the practice's behalf — change billing cycle, pause, resume, or cancel.",
    requiresManagedBilling: true,
  },
]
// Schema validates against all 5 values always — the UI hides Subscription
// Manager when MB is off and a useEffect strips orphan values, so any data
// reaching the schema is already correct. This avoids the RHF resolver-cache
// gotcha (useForm captures the resolver once at init).
const ALL_PERMISSION_VALUES = ALL_PERMISSIONS.map((p) => p.value)

const strictSchema = z
  .object({
    teams: z
      .array(
        z.object({
          fullName: z.string().refine((v) => validateName(v).isValid, {
            message: 'Add a full name (first and last)',
          }),
          email: z.string().refine(isValidEmail, {
            message: 'Add a work email',
          }),
          phoneNo: z.string().refine(isValidPhone, {
            message: 'Phone must be 10 digits',
          }),
          permissions: z
            .array(z.enum(ALL_PERMISSION_VALUES))
            .min(1, 'Select at least one permission'),
          locationIds: z
            .array(z.string())
            .min(1, 'Select at least one location'),
          jobDescription: z.string().optional(),
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    const list = data.teams || []
    const seen = new Map()
    list.forEach((t, i) => {
      const key = String(t?.email ?? '').trim().toLowerCase()
      if (!key) return
      if (seen.has(key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['teams', i, 'email'],
          message: 'Each team member needs a unique email',
        })
      } else {
        seen.set(key, i)
      }
    })
  })
const looseSchema = z.object({
  teams: z.array(z.any()).optional(),
}).passthrough()
const schema = isValidationDisabled() ? looseSchema : strictSchema

const emptyTeam = {
  fullName: '',
  email: '',
  phoneNo: '',
  permissions: [],
  locationIds: [],
}

function buildLocationOptions(locations) {
  if (!Array.isArray(locations)) return []
  return locations
    .filter((l) => l?.id)
    .map((l, i) => {
      const label = (l?.addressLine1 || '').trim() || `Location ${i + 1}`
      return { value: l.id, label }
    })
}

function ownerDisplayName(owner, idx) {
  const n = (owner?.fullName || '').trim()
  return n || `Owner ${idx + 1}`
}

// Role presets — instead of asking the user to assemble raw permissions, we
// offer 4 named roles that map to a permission set. Copy adapts to whether
// Managed Billing is opted in (subscription-management language only appears
// when MB is on). The saved permissions array is unchanged — presets just
// auto-fill it, so the schema and backend see the same values as before.
function getRolePresets(managedBillingEnabled) {
  if (managedBillingEnabled) {
    return [
      {
        id: 'front-desk',
        label: 'Front desk',
        who: 'Receptionist, office admin',
        description:
          'Invite patients, check registration status, update billing cycles, cancel subscriptions. Manage patient subscription packages, apply coupons, and handle enrollment.',
        permissions: ['assistant', 'subscription manager'],
        icon: '🏥',
      },
      {
        id: 'clinical',
        label: 'Clinical staff',
        who: 'Nurse, MA, clinical coordinator',
        description:
          'Everything front desk can do, plus view and manage patient health records.',
        permissions: ['assistant', 'medical assistant', 'subscription manager'],
        icon: '🩺',
      },
      {
        id: 'accountant',
        label: 'Accountant',
        who: 'CPA, bookkeeper, billing lead',
        description:
          "All invoices, transactions, payments, refunds, CSV exports, reconciliation data. Your practice's billing with BRITE — not patient subscriptions.",
        permissions: ['finance'],
        icon: '💰',
      },
      {
        id: 'practice-manager',
        label: 'Practice manager',
        who: 'Office manager, ops lead',
        description:
          'Full Operations Hub access — patient management, health records, practice billing, analytics, and patient subscription management. Everything in one place.',
        permissions: ['assistant', 'medical assistant', 'finance', 'analytics', 'subscription manager'],
        icon: '📊',
      },
    ]
  }

  // Managed billing NOT opted in — no subscription management language.
  return [
    {
      id: 'front-desk',
      label: 'Front desk',
      who: 'Receptionist, office admin',
      description:
        'Invite patients, check registration status, update billing cycles, cancel patient subscriptions.',
      permissions: ['assistant'],
      icon: '🏥',
    },
    {
      id: 'clinical',
      label: 'Clinical staff',
      who: 'Nurse, MA, clinical coordinator',
      description:
        'Everything front desk can do, plus view and manage patient health records.',
      permissions: ['assistant', 'medical assistant'],
      icon: '🩺',
    },
    {
      id: 'accountant',
      label: 'Accountant',
      who: 'CPA, bookkeeper, billing lead',
      description:
        "All invoices, transactions, payments, refunds, CSV exports, reconciliation data. Your practice's billing with BRITE.",
      permissions: ['finance'],
      icon: '💰',
    },
    {
      id: 'practice-manager',
      label: 'Practice manager',
      who: 'Office manager, ops lead',
      description:
        'Full Operations Hub access — patient management, health records, practice billing, and analytics. Everything in one place.',
      permissions: ['assistant', 'medical assistant', 'finance', 'analytics'],
      icon: '📊',
    },
  ]
}

// Reverse-map a saved permissions array to the preset it represents. Empty →
// null (nothing picked yet, e.g. a freshly-seeded card). Non-empty with no
// exact match → 'practice-manager' (safe default for stray old data).
function matchPresetId(permArr, presets) {
  const set = new Set(permArr || [])
  if (set.size === 0) return null
  const found = presets.find(
    (p) => p.permissions.length === set.size && p.permissions.every((x) => set.has(x)),
  )
  return found ? found.id : 'practice-manager'
}

// A row counts as "touched" if the user has entered anything at all. Untouched
// seeded rows are stripped on submit so a solo practice isn't blocked by the
// two empty starter cards.
function isRowTouched(t) {
  if (!t) return false
  const s = (v) => String(v ?? '').trim()
  return !!(
    s(t.fullName) ||
    s(t.email) ||
    s(t.phoneNo) ||
    (Array.isArray(t.permissions) && t.permissions.length) ||
    (Array.isArray(t.locationIds) && t.locationIds.length)
  )
}

export default function Step8Teams() {
  const step = STEPS.find((s) => s.id === STEP_ID)
  const persisted = useOnboardingStore((s) => s.formData[STEP_ID])
  const liteMode = useOnboardingStore((s) => s.liteMode)
  const locationsData = useOnboardingStore(
    (s) => s.formData.locations?.locations,
  )
  const ownersData = useOnboardingStore((s) => s.formData?.owners?.owners)
  const providersData = useOnboardingStore(
    (s) => s.formData?.providers?.providers,
  )
  // Managed Billing flag — when off, hide Subscription Manager role.
  const managedBillingAcknowledged = useOnboardingStore(
    (s) => s.formData?.managedBilling?.acknowledged === true,
  )
  const suggested = useOnboardingStore((s) => s.formData?.suggested)
  const practiceWebsite = useOnboardingStore(
    (s) => s.formData?.details?.website,
  )
  const suggestedDomain =
    suggested?.emailDomain || extractEmailDomain(practiceWebsite) || null
  const updateFormData = useOnboardingStore((s) => s.updateFormData)
  const nextStep = useOnboardingStore((s) => s.nextStep)
  const { setHandler, clearHandler, reportErrorCount, pulse, focusFirstError } =
    useStepSubmit()

  // Role presets adapt their copy + permission sets to Managed Billing status.
  const rolePresets = useMemo(
    () => getRolePresets(managedBillingAcknowledged),
    [managedBillingAcknowledged],
  )

  const locationOptions = useMemo(
    () => buildLocationOptions(locationsData),
    [locationsData],
  )
  // Team members default to working at ALL locations (multi-select; deselectable).
  const allLocationIds = useMemo(
    () => locationOptions.map((o) => o.value),
    [locationOptions],
  )

  const owners = Array.isArray(ownersData) ? ownersData : []

  const defaultValues = useMemo(
    () => ({
      teams: persisted?.teams ?? [],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const {
    control,
    register,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })


  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'teams',
  })
  const watched = useWatch({ control, name: 'teams' })

  // Primary contact = one team member designated as the point of contact for
  // patient issues. No extra fields — reuses their name/email/phone. Tracked by
  // row index; a ref mirrors it so the submit handler reads a fresh value and
  // can remap the index when untouched rows are stripped.
  const [primaryContactIdx, setPrimaryContactIdx] = useState(
    persisted?.primaryContactIndex ?? 0,
  )
  const primaryIdxRef = useRef(persisted?.primaryContactIndex ?? 0)
  useEffect(() => {
    primaryIdxRef.current = primaryContactIdx
  }, [primaryContactIdx])
  const setPrimaryContact = (idx) => setPrimaryContactIdx(idx)

  // "It's just me" skip — shows a confirmation modal before advancing, so the
  // important team step isn't skipped on an accidental tap.
  const [showSkipConfirm, setShowSkipConfirm] = useState(false)

  const onValid = (data) => {
    const teams = data.teams ?? []
    const idx = primaryIdxRef.current
    const pm = idx != null && idx >= 0 ? teams[idx] : null
    const owner0 = owners?.[0]
    const primaryContact = pm
      ? { name: pm.fullName, email: pm.email, phone: pm.phoneNo, source: 'team' }
      : owner0
        ? { name: owner0.fullName, email: owner0.email, phone: owner0.phoneNo, source: 'owner' }
        : null
    updateFormData(STEP_ID, {
      teams,
      primaryContact,
      primaryContactIndex: pm ? idx : null,
    })
    nextStep()
  }
  const { submitAttempted, errorMessages, onInvalid } = useStepErrorSummary(
    STEP_ID,
    errors,
  )

  useEffect(() => {
    const submit = handleSubmit(onValid, onInvalid)
    setHandler(() => {
      // Drop fully-untouched starter cards before validating, so the two
      // seeded slots never block a practice that has no extra staff to add.
      const current = getValues('teams') || []
      const touched = current.filter(isRowTouched)
      if (touched.length !== current.length) {
        // Re-point the primary-contact index at the kept row's new position
        // (filter preserves object identity, so indexOf is reliable).
        const idx = primaryIdxRef.current
        const primaryMember = idx != null && idx >= 0 ? current[idx] : null
        const newIdx = primaryMember ? touched.indexOf(primaryMember) : -1
        primaryIdxRef.current = newIdx >= 0 ? newIdx : null
        setPrimaryContactIdx(primaryIdxRef.current)
        replace(touched)
      }
      // Genuine solo practice — no touched rows. Nothing to validate; advance.
      if (touched.length === 0) {
        onValid({ teams: [] })
        return
      }
      // Otherwise EVERY kept (touched) row must pass the schema before we move
      // on. handleSubmit runs onValid only when validation passes; a failure
      // routes to onInvalid (→ focusFirstError) and never reaches nextStep, so a
      // partially-filled invalid row can't advance.
      return submit()
    })
    return () => setHandler(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, setHandler])

  // Pre-seed two empty team slots on a fresh visit so the step reads as
  // "fill these in" rather than "build something from scratch". Skipped for
  // returning users who already have (or deliberately cleared) team data.
  const seededRef = useRef(false)
  useEffect(() => {
    if (seededRef.current) return
    seededRef.current = true
    if (
      fields.length === 0 &&
      (!persisted?.teams || persisted.teams.length === 0)
    ) {
      append({ ...emptyTeam, locationIds: allLocationIds })
      append({ ...emptyTeam, locationIds: allLocationIds })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    reportErrorCount(flattenErrorCount(errors))
  }, [errors, reportErrorCount])

  useEffect(() => {
    return () => reportErrorCount(0)
  }, [reportErrorCount])

  useEffect(() => {
    if (persisted && Object.keys(persisted).length > 0) trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When MB toggles off (or on first mount with stale data), silently drop
  // any 'subscription manager' values — the role isn't selectable in this
  // mode and we don't want to fail validation on it.
  useEffect(() => {
    if (managedBillingAcknowledged) return
    const teams = watched || []
    teams.forEach((t, i) => {
      const perms = Array.isArray(t?.permissions) ? t.permissions : []
      if (!perms.includes('subscription manager')) return
      const cleaned = perms.filter((p) => p !== 'subscription manager')
      setValue(`teams.${i}.permissions`, cleaned, {
        shouldDirty: true,
        shouldValidate: false,
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managedBillingAcknowledged, fields.length])

  // Cross-step email uniqueness: compare teams against providers only.
  // Owner overlap is intentionally allowed (legit dual-role); the backend
  // silently aliases the owner email to +hub for the sheet row.
  useEffect(() => {
    const currentTeams = watched || []
    const dupes = uniqueEmailsAcross({
      providers: providersData,
      teams: currentTeams,
    })
    currentTeams.forEach((t, i) => {
      const email = String(t?.email ?? '').trim().toLowerCase()
      if (!email) return
      const locations = dupes.get(email)
      if (!locations) {
        const cur = errors?.teams?.[i]?.email
        if (cur?.type === 'cross-step') clearErrors(`teams.${i}.email`)
        return
      }
      const other = locations.find(
        (l) => !(l.group === 'teams' && l.index === i),
      )
      if (!other) {
        const cur = errors?.teams?.[i]?.email
        if (cur?.type === 'cross-step') clearErrors(`teams.${i}.email`)
        return
      }
      setError(`teams.${i}.email`, {
        type: 'cross-step',
        message: 'Already used by provider. Each person needs their own email.',
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched, providersData])

  const addStaff = () => append({ ...emptyTeam, locationIds: allLocationIds })

  const hasAnyTouched = (watched || []).some(isRowTouched)

  return (
    <form
      className={`step-body${liteMode ? ' is-lite-teams' : ''}`}
      onSubmit={handleSubmit(onValid, onInvalid)}
      noValidate
    >
      <StepLayout
        question="Who else needs access?"
        helper="Your team gets their own login to the Operations Hub — your practice's staff portal for patients, billing, and day-to-day ops. Most practices add 2–3 members. Owners already have full access."
      >
      <Band label="Team access" description="Staff who need Operations Hub login">
      <ErrorBanner show={submitAttempted} messages={errorMessages} />
      <p className="team-owner-note">
        {owners.length > 0
          ? `${owners.map((o, i) => ownerDisplayName(o, i)).join(', ')} already ${owners.length === 1 ? 'has' : 'have'} full access as practice ${owners.length === 1 ? 'owner' : 'owners'}.`
          : 'Practice owners automatically get full access.'}
      </p>

      {/* Skip option — surfaced at the top as a clear secondary action. Opens a
          confirmation before advancing (logic gate unchanged otherwise). */}
      {!hasAnyTouched &&
        (liteMode ? (
          <p className="team-skip-helper">
            It's just me — just hit Continue <span aria-hidden="true">→</span>
          </p>
        ) : (
          <button
            type="button"
            className="team-skip-cta"
            onClick={() => setShowSkipConfirm(true)}
          >
            Skip — it's just me <span aria-hidden="true">→</span>
          </button>
        ))}

      <div className="step-section">
        {fields.length >= 1 && (
          <div className="teams-staff-column teams-staff-column--full">
            <EntryHeader noun="team member" count={fields.length} onAdd={addStaff} />
            {renderStaffCards()}
            <AddAnotherButton
              label="Add another staff member"
              onClick={addStaff}
            />
          </div>
        )}

        {fields.length === 0 && (
          <AddAnotherButton label="Add staff member" onClick={addStaff} />
        )}

        {fields.length === 0 && owners.length > 0 && (
          <p className="primary-contact-auto">
            <strong>{ownerDisplayName(owners[0], 0)}</strong> will be the primary
            contact for patient issues since no team members are added.
          </p>
        )}
      </div>
      </Band>
      </StepLayout>

      {showSkipConfirm && (
        <div
          className="skip-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="skip-confirm-title"
          onClick={() => setShowSkipConfirm(false)}
        >
          <div
            className="skip-confirm-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="skip-confirm-title" className="skip-confirm-title">
              Are you sure?
            </h3>
            <p className="skip-confirm-body">
              Adding your team now saves time later. Your staff will need access
              to the Operations Hub to manage patients, billing, and
              prescriptions. You can always add them later, but setting it up now
              means they're ready on day one.
            </p>
            <div className="skip-confirm-actions">
              <button
                type="button"
                className="skip-confirm-primary"
                onClick={() => setShowSkipConfirm(false)}
              >
                Go back and add team
              </button>
              <button
                type="button"
                className="skip-confirm-ghost"
                onClick={() => {
                  setShowSkipConfirm(false)
                  nextStep()
                }}
              >
                Skip for now <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <button type="submit" className="sr-only" tabIndex={-1}>
        Continue
      </button>
    </form>
  )

  function renderStaffCards() {
    return fields.map((f, idx) => {
          const rowErr = errors?.teams?.[idx]
          const t = watched?.[idx] || {}
          const emailVal = t.email
          const emailSuggestion = suggestEmailCorrection(emailVal)
          const label = ordinalLabel(idx, 'team member')
          const namePlaceholder =
            idx === 0
              ? 'e.g. Sarah — front desk'
              : idx === 1
                ? 'e.g. Mike — your accountant'
                : 'Full legal name'
          const isPrimaryContact = primaryContactIdx === idx
          const cardActions = (
            <div className="team-card-header-actions">
              <button
                type="button"
                className={`primary-contact-toggle${isPrimaryContact ? ' is-active' : ''}`}
                onClick={() => setPrimaryContact(isPrimaryContact ? null : idx)}
                title="Designate as primary contact for patient issues"
              >
                {isPrimaryContact ? '★ Primary contact' : '☆ Make primary contact'}
              </button>
              <button
                type="button"
                className="team-card-remove"
                aria-label="Remove this team member"
                onClick={() => remove(idx)}
              >
                ×
              </button>
            </div>
          )
          return (
            <Card key={f.id} title={label} actions={cardActions}>
              <FieldRow cols={3}>
                <Field
                  label="Full name"
                  htmlFor={`team-${idx}-name`}
                  error={rowErr?.fullName}
                >
                  <RowInput
                    id={`team-${idx}-name`}
                    type="text"
                    placeholder={namePlaceholder}
                    autoComplete="name"
                    error={!!rowErr?.fullName}
                    {...register(`teams.${idx}.fullName`)}
                  />
                </Field>

                <Field
                  label="Phone"
                  htmlFor={`team-${idx}-phone`}
                  error={rowErr?.phoneNo}
                >
                  <Controller
                    name={`teams.${idx}.phoneNo`}
                    control={control}
                    render={({ field }) => (
                      <RowInput
                        id={`team-${idx}-phone`}
                        type="phone"
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="(123) 456-7890"
                        autoComplete="tel"
                        error={!!rowErr?.phoneNo}
                      />
                    )}
                  />
                </Field>

                <Field
                  label="Work email"
                  htmlFor={`team-${idx}-email`}
                  error={emailSuggestion ? null : rowErr?.email}
                >
                  <Controller
                    name={`teams.${idx}.email`}
                    control={control}
                    render={({ field }) => (
                      <EmailInputWithDomain
                        id={`team-${idx}-email`}
                        value={field.value}
                        onChange={field.onChange}
                        suggestedDomain={suggestedDomain}
                        placeholder={
                          suggestedDomain
                            ? `you@${suggestedDomain}`
                            : 'Work email'
                        }
                        autoComplete="email"
                        error={!!rowErr?.email && !emailSuggestion}
                      />
                    )}
                  />
                  {emailSuggestion && (
                    <div className="email-suggestion">
                      Did you mean{' '}
                      <button
                        type="button"
                        className="email-suggestion-accept"
                        onClick={() =>
                          setValue(
                            `teams.${idx}.email`,
                            emailSuggestion,
                            { shouldValidate: true, shouldDirty: true },
                          )
                        }
                      >
                        {emailSuggestion}
                      </button>
                      ?
                    </div>
                  )}
                </Field>
              </FieldRow>

              <FieldRow cols={1}>
                <Field label="What does this person do?" error={rowErr?.permissions}>
                  {liteMode && (
                    <p className="role-pick-reassurance">
                      Not sure which role to pick? Choose what feels closest —
                      you can{' '}
                      <span className="role-pick-reassurance-accent">
                        change it anytime
                      </span>{' '}
                      from your dashboard.
                    </p>
                  )}
                  <Controller
                    name={`teams.${idx}.permissions`}
                    control={control}
                    render={({ field }) => {
                      const selectedRoleId = matchPresetId(field.value, rolePresets)
                      return (
                        <div className="role-preset-grid">
                          {rolePresets.map((preset) => {
                            const isSelected = selectedRoleId === preset.id
                            return (
                              <button
                                key={preset.id}
                                type="button"
                                className={`role-preset-card${isSelected ? ' is-selected' : ''}`}
                                onClick={() => field.onChange(preset.permissions)}
                              >
                                <div className="role-preset-top">
                                  <span className="role-preset-icon">{preset.icon}</span>
                                  <span className="role-preset-label">{preset.label}</span>
                                </div>
                                <p className="role-preset-who">{preset.who}</p>
                                <p className="role-preset-desc">{preset.description}</p>
                              </button>
                            )
                          })}
                        </div>
                      )
                    }}
                  />
                </Field>
              </FieldRow>

              <FieldRow cols={1}>
                <Field label="Locations they work at" error={rowErr?.locationIds}>
                  <Controller
                    name={`teams.${idx}.locationIds`}
                    control={control}
                    render={({ field }) =>
                      locationOptions.length === 0 ? (
                        <div className="team-card-empty-hint">
                          Add at least one location in Step 5 to assign team
                          members.
                        </div>
                      ) : (
                        <div className="team-card-chips">
                          {locationOptions.map((opt) => {
                            const selected = (field.value || []).includes(
                              opt.value,
                            )
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                className="team-card-chip"
                                aria-pressed={selected}
                                onClick={() => {
                                  const next = new Set(field.value || [])
                                  if (next.has(opt.value)) next.delete(opt.value)
                                  else next.add(opt.value)
                                  field.onChange(Array.from(next))
                                }}
                              >
                                {opt.label}
                              </button>
                            )
                          })}
                        </div>
                      )
                    }
                  />
                  {locationOptions.length > 0 && (
                    <p className="team-card-loc-hint">
                      All locations selected — tap to deselect any.
                    </p>
                  )}
                </Field>
              </FieldRow>
            </Card>
          )
        })
  }
}
